import { getActiveTimezone } from '@/utils/timezone';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Banknote, Save, Loader2, Info, Lock, Search, RotateCcw, Trash2, ShieldCheck, ShieldOff, KeyRound } from 'lucide-react';
import { companyApi, CashSettlementSettings as CashSettlementSettingsType } from '@/services/companyApi';
import { employeeApi } from '@/services/employeeApi';
import { securityPinApi } from '@/services/securityPinApi';
import { branchApi } from '@/services/branchApi';
import SecurityPinModal from '@/components/common/SecurityPinModal';
import SimpleSelect from '@/components/common/SimpleSelect';

export default function CashSettlementSettings() {
  const queryClient = useQueryClient();
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // PIN management state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBranchId, setFilterBranchId] = useState('');
  const [resetTarget, setResetTarget] = useState<{ id: string; name: string } | null>(null);
  const [removeTarget, setRemoveTarget] = useState<{ id: string; name: string } | null>(null);
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [showPinChange, setShowPinChange] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);

  // Fetch current settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['cashSettlementSettings'],
    queryFn: companyApi.getCashSettlementSettings,
  });

  // Fetch admin's own PIN status
  const { data: myPinStatus, isLoading: pinStatusLoading } = useQuery({
    queryKey: ['myPinStatus'],
    queryFn: securityPinApi.getStatus,
  });

  // Fetch all employees (large limit to get all)
  const { data: employeeData, isLoading: employeesLoading } = useQuery({
    queryKey: ['employees-pin-management', filterBranchId],
    queryFn: () => employeeApi.getAllEmployees(
      { branchId: filterBranchId || undefined, isActive: true },
      1,
      200
    ),
  });

  // Fetch branches for filter
  const { data: branchData } = useQuery({
    queryKey: ['branches-list-settings'],
    queryFn: () => branchApi.getAllBranches(undefined, 1, 100),
  });

  // Update settings mutation
  const updateMutation = useMutation({
    mutationFn: (data: CashSettlementSettingsType) => companyApi.updateCashSettlementSettings(data),
    onSuccess: () => {
      toast.success('Cash settlement settings updated successfully');
      queryClient.invalidateQueries({ queryKey: ['cashSettlementSettings'] });
      queryClient.invalidateQueries({ queryKey: ['company'] });
      setHasChanges(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update settings');
    },
  });

  // Setup PIN mutation
  const setupPinMutation = useMutation({
    mutationFn: (pin: string) => securityPinApi.setupPin(pin),
    onSuccess: () => {
      toast.success('Security PIN set successfully');
      queryClient.invalidateQueries({ queryKey: ['myPinStatus'] });
      setShowPinSetup(false);
      setPinError(null);
    },
    onError: (error: any) => {
      setPinError(error.response?.data?.message || 'Failed to set PIN');
    },
  });

  // Change PIN mutation (uses setup modal for new PIN after verifying current)
  const changePinMutation = useMutation({
    mutationFn: ({ currentPin, newPin }: { currentPin: string; newPin: string }) =>
      securityPinApi.changePin(currentPin, newPin),
    onSuccess: () => {
      toast.success('Security PIN changed successfully');
      queryClient.invalidateQueries({ queryKey: ['myPinStatus'] });
      setShowPinChange(false);
      setPinError(null);
    },
    onError: (error: any) => {
      setPinError(error.response?.data?.message || 'Failed to change PIN');
    },
  });

  // Admin reset PIN mutation
  const resetPinMutation = useMutation({
    mutationFn: ({ userId, newPin }: { userId: string; newPin: string }) =>
      securityPinApi.adminResetPin(userId, newPin),
    onSuccess: () => {
      toast.success(`PIN reset successfully for ${resetTarget?.name}`);
      queryClient.invalidateQueries({ queryKey: ['employees-pin-management'] });
      setResetTarget(null);
      setPinError(null);
    },
    onError: (error: any) => {
      setPinError(error.response?.data?.message || 'Failed to reset PIN');
    },
  });

  // Admin remove PIN mutation
  const removePinMutation = useMutation({
    mutationFn: (userId: string) => securityPinApi.adminRemovePin(userId),
    onSuccess: () => {
      toast.success(`PIN removed for ${removeTarget?.name}`);
      queryClient.invalidateQueries({ queryKey: ['employees-pin-management'] });
      setRemoveTarget(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to remove PIN');
    },
  });

  // Load settings into state
  useEffect(() => {
    if (settings) {
      setAllowMultiple(settings.allowMultipleSettlements);
    }
  }, [settings]);

  const handleChange = (value: boolean) => {
    setAllowMultiple(value);
    setHasChanges(value !== settings?.allowMultipleSettlements);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({ allowMultipleSettlements: allowMultiple });
  };

  const handleReset = () => {
    if (settings) {
      setAllowMultiple(settings.allowMultipleSettlements);
      setHasChanges(false);
    }
  };

  // Filter employees by search
  const filteredEmployees = (employeeData?.employees || []).filter((emp) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      emp.name.toLowerCase().includes(term) ||
      emp.email.toLowerCase().includes(term) ||
      emp.branch?.name?.toLowerCase().includes(term)
    );
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Banknote className="w-6 h-6 text-purple-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Cash Settlement Settings</h2>
          <p className="text-sm text-gray-500">
            Configure how cash settlements are handled per day
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Settlement Mode */}
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-4">Settlement Mode</h3>

          <div className="space-y-4">
            {/* Single Settlement Option */}
            <label
              className={`flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                !allowMultiple
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="settlementMode"
                checked={!allowMultiple}
                onChange={() => handleChange(false)}
                className="mt-1 w-4 h-4 text-purple-600 focus:ring-purple-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">Single Settlement Per Day</span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    Recommended
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  All payments collected throughout the day are settled in one settlement.
                  This is the standard approach for most branches.
                </p>
              </div>
            </label>

            {/* Multiple Settlements Option */}
            <label
              className={`flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                allowMultiple
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="settlementMode"
                checked={allowMultiple}
                onChange={() => handleChange(true)}
                className="mt-1 w-4 h-4 text-purple-600 focus:ring-purple-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">Multiple Settlements Per Day</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    High Volume
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  After verifying a settlement, a new settlement can be created for remaining payments.
                  Useful for high-volume branches with multiple shifts.
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Info Box */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">How Multiple Settlements Work:</p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-blue-700">
              <li>Settlement #1 is created at the start of the day</li>
              <li>After verifying Settlement #1, any new payments trigger Settlement #2</li>
              <li>Each settlement covers a specific time period</li>
              <li>History shows all settlements with their sequence numbers</li>
            </ul>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={handleReset}
            disabled={!hasChanges || updateMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
          <button
            type="submit"
            disabled={!hasChanges || updateMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>

      {/* Security PIN Management Section */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center gap-3 mb-6">
          <Lock className="w-6 h-6 text-purple-600" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Security PIN Management</h2>
            <p className="text-sm text-gray-500">
              Manage security PINs for cash settlement verification
            </p>
          </div>
        </div>

        {/* Admin's Own PIN Status */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                myPinStatus?.hasPin ? 'bg-green-100' : 'bg-gray-200'
              }`}>
                <KeyRound className={`w-5 h-5 ${myPinStatus?.hasPin ? 'text-green-600' : 'text-gray-500'}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Your PIN Status</p>
                {pinStatusLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400 mt-0.5" />
                ) : myPinStatus?.hasPin ? (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    PIN is set
                    {myPinStatus.setAt && (
                      <span className="text-gray-400 ml-1">
                        (since {new Date(myPinStatus.setAt).toLocaleDateString(undefined, { timeZone: getActiveTimezone() })})
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <ShieldOff className="w-3.5 h-3.5" />
                    No PIN configured
                  </p>
                )}
              </div>
            </div>
            <div>
              {myPinStatus?.hasPin ? (
                <button
                  type="button"
                  onClick={() => { setShowPinChange(true); setPinError(null); }}
                  className="px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  Change PIN
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => { setShowPinSetup(true); setPinError(null); }}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Setup PIN
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Employee PINs Section */}
        <div className="bg-gray-50 rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Employee PINs</h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, or branch..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <SimpleSelect
                value={filterBranchId}
                onChange={setFilterBranchId}
                options={[
                  { value: '', label: 'All Branches' },
                  ...(branchData?.branches ?? []).map((branch) => ({
                    value: branch.id,
                    label: branch.name,
                  })),
                ]}
                placeholder="All Branches"
                aria-label="Filter by branch"
                wrapperClassName="w-full sm:w-48"
                className="focus:!ring-purple-500 focus:!border-purple-500"
              />
            </div>
          </div>

          {employeesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">
              No employees found
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Employee</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Branch</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Role</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-600">PIN Status</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredEmployees.map((emp) => {
                      const hasPin = !!emp.securityPinSetAt;
                      return (
                        <tr key={emp.id} className="hover:bg-white transition-colors">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-gray-900">{emp.name}</p>
                              <p className="text-xs text-gray-500">{emp.email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-700">
                            {emp.branch?.name || '-'}
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                              {emp.role?.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            {hasPin ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                                <ShieldCheck className="w-3 h-3" />
                                Set
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                <ShieldOff className="w-3 h-3" />
                                Not Set
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => { setResetTarget({ id: emp.id, name: emp.name }); setPinError(null); }}
                                className="px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                                title={hasPin ? 'Reset PIN' : 'Set PIN'}
                              >
                                <RotateCcw className="w-3.5 h-3.5 inline mr-1" />
                                {hasPin ? 'Reset' : 'Set'}
                              </button>
                              {hasPin && (
                                <button
                                  type="button"
                                  onClick={() => setRemoveTarget({ id: emp.id, name: emp.name })}
                                  className="px-2.5 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                                  title="Remove PIN"
                                >
                                  <Trash2 className="w-3.5 h-3.5 inline mr-1" />
                                  Remove
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y divide-gray-100">
                {filteredEmployees.map((emp) => {
                  const hasPin = !!emp.securityPinSetAt;
                  return (
                    <div key={emp.id} className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-gray-900">{emp.name}</p>
                          <p className="text-xs text-gray-500">{emp.email}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {emp.branch?.name || '-'} &middot; {emp.role?.replace(/_/g, ' ')}
                          </p>
                        </div>
                        {hasPin ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                            <ShieldCheck className="w-3 h-3" />
                            Set
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                            <ShieldOff className="w-3 h-3" />
                            Not Set
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          type="button"
                          onClick={() => { setResetTarget({ id: emp.id, name: emp.name }); setPinError(null); }}
                          className="flex-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors text-center"
                        >
                          <RotateCcw className="w-3.5 h-3.5 inline mr-1" />
                          {hasPin ? 'Reset PIN' : 'Set PIN'}
                        </button>
                        {hasPin && (
                          <button
                            type="button"
                            onClick={() => setRemoveTarget({ id: emp.id, name: emp.name })}
                            className="flex-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors text-center"
                          >
                            <Trash2 className="w-3.5 h-3.5 inline mr-1" />
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Setup PIN Modal (Admin's own) */}
      <SecurityPinModal
        isOpen={showPinSetup}
        onClose={() => { setShowPinSetup(false); setPinError(null); }}
        onVerify={async (pin) => {
          setupPinMutation.mutate(pin);
        }}
        title="Setup Security PIN"
        description="Enter a 4-6 digit PIN for settlement verification"
        isLoading={setupPinMutation.isPending}
        error={pinError}
      />

      {/* Change PIN Modal (Admin's own) */}
      <ChangePinFlow
        isOpen={showPinChange}
        onClose={() => { setShowPinChange(false); setPinError(null); }}
        onSubmit={(currentPin, newPin) => {
          changePinMutation.mutate({ currentPin, newPin });
        }}
        isLoading={changePinMutation.isPending}
        error={pinError}
      />

      {/* Reset PIN Modal (Admin resets for employee) */}
      <SecurityPinModal
        isOpen={!!resetTarget}
        onClose={() => { setResetTarget(null); setPinError(null); }}
        onVerify={async (pin) => {
          if (resetTarget) {
            resetPinMutation.mutate({ userId: resetTarget.id, newPin: pin });
          }
        }}
        title={`${resetTarget?.name ? `Set PIN for ${resetTarget.name}` : 'Set PIN'}`}
        description="Enter a new 4-6 digit PIN for this employee"
        isLoading={resetPinMutation.isPending}
        error={pinError}
      />

      {/* Remove PIN Confirmation Modal */}
      {removeTarget && (
        <RemovePinConfirmModal
          employeeName={removeTarget.name}
          isLoading={removePinMutation.isPending}
          onConfirm={() => removePinMutation.mutate(removeTarget.id)}
          onClose={() => setRemoveTarget(null)}
        />
      )}
    </div>
  );
}

// ==================== Change PIN Flow ====================
// Step 1: Enter current PIN, Step 2: Enter new PIN

function ChangePinFlow({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  error,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (currentPin: string, newPin: string) => void;
  isLoading: boolean;
  error: string | null;
}) {
  const [step, setStep] = useState<'current' | 'new'>('current');
  const [currentPin, setCurrentPin] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStep('current');
      setCurrentPin('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  if (step === 'current') {
    return (
      <SecurityPinModal
        isOpen={true}
        onClose={onClose}
        onVerify={async (pin) => {
          setCurrentPin(pin);
          setStep('new');
        }}
        title="Change PIN - Current PIN"
        description="Enter your current security PIN"
        isLoading={false}
        error={null}
      />
    );
  }

  return (
    <SecurityPinModal
      isOpen={true}
      onClose={onClose}
      onVerify={async (pin) => {
        onSubmit(currentPin, pin);
      }}
      title="Change PIN - New PIN"
      description="Enter your new 4-6 digit PIN"
      isLoading={isLoading}
      error={error}
    />
  );
}

// ==================== Remove PIN Confirmation Modal ====================

function RemovePinConfirmModal({
  employeeName,
  isLoading,
  onConfirm,
  onClose,
}: {
  employeeName: string;
  isLoading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const handleBackdrop = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={handleBackdrop}>
      <div className="fixed inset-0 bg-black bg-opacity-50" />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-sm bg-white rounded-xl shadow-2xl p-6 text-center">
          <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <Trash2 className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Remove PIN</h3>
          <p className="text-sm text-gray-600 mb-1">
            Remove security PIN for <strong>{employeeName}</strong>?
          </p>
          <p className="text-xs text-gray-500 mb-6">
            This employee will no longer be able to verify or reject settlements until a new PIN is set.
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Remove'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
